"use client";

import { useCallback, useMemo } from "react";
import { Check } from "lucide-react";
import { Button } from "@stockline/ui/components/button";
import { useTranslation } from "react-i18next";
import type { ChatMessage } from "@/app/api/types/chat-sessions";
import { isPricingScopeData } from "./user-input/types";
import { PricingScopeCard } from "./user-input/PricingScopeCard";

interface UserInputCardProps {
  readonly message: ChatMessage;
  readonly onSendMessage: (message: string) => void;
}

function GenericInputCard({ onConfirm }: { readonly onConfirm: () => void }) {
  const { t } = useTranslation();

  return (
    <div
      className="rounded-lg border border-violet-200 bg-violet-50/30 dark:border-violet-800 dark:bg-violet-900/10 overflow-hidden"
      data-testid="user-input-card"
    >
      <div className="flex items-center justify-end gap-2 px-3 py-2">
        <Button
          size="sm"
          onClick={onConfirm}
          className="h-7 gap-1.5 text-xs"
          data-testid="user-input-confirm"
        >
          <Check className="h-3 w-3" />
          {t("ai.userInput.confirm", "Confirmer")}
        </Button>
      </div>
    </div>
  );
}

export function UserInputCard({ message, onSendMessage }: UserInputCardProps) {
  const structuredData = useMemo(() => {
    const toolResult = message.tool_results?.find(
      (tr) => tr.tool_use_id === "request_user_input",
    );
    return toolResult?.rich_content;
  }, [message.tool_results]);

  const handlePricingConfirm = useCallback(
    (level: string, scope: string, targetId: string | null) => {
      onSendMessage(
        JSON.stringify({
          level,
          scope,
          ...(targetId ? { target_id: targetId } : {}),
        }),
      );
    },
    [onSendMessage],
  );

  const handleGenericConfirm = useCallback(() => {
    onSendMessage("confirmed");
  }, [onSendMessage]);

  if (isPricingScopeData(structuredData)) {
    return (
      <PricingScopeCard
        data={structuredData}
        onConfirm={handlePricingConfirm}
      />
    );
  }

  return <GenericInputCard onConfirm={handleGenericConfirm} />;
}
